import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { getSpecialities } from 'src/modules/app/api/appApi';
import InputSelect from 'src/components/shared/Form/Select';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import theme from 'src/config/theme';
import {
    AddContainer, Component4,
    Texts,
    InputTextComponent,
} from './Styles';

function ProfessionalInfoStep({ innerRef, data, setDoctorProfileData }) {
    const initialValues = {
        experience: data?.experience || '',
        degree: (data?.degree || '').split(',').map(d => d.trim().toLowerCase()),
        licenceInfo: data?.licenceInfo || '',
        professionalExperience: data?.professionalExperience || '',
        challenges: data?.challenges || '',
        awardsAchievements: data?.awardsAchievements || '',
        specialProcedure: data?.specialProcedure || '',
        requestToPatients: data?.requestToPatients || '',
        speciality: data?.speciality || '',
        superSpeciality: data?.superSpeciality || ''
    }
    
    const [specialitiesOptions, setSpecialitiesOptions] = useState([]);
    const [superSpecialitiesMap, setSuperSpecialitiesMap] = useState({});

    useEffect(() => {
        const fetchSpecialities = async () => {
            try {
                const response = await getSpecialities();
                const data = response?.data || response || [];
                
                // main specialities don't have a parentId
                const main = data.filter(s => !s.parentId);
                const options = main.map(s => ({ label: s.name, value: s.id }));
                setSpecialitiesOptions(options);

                // Map super specialities
                const map = {};
                main.forEach(m => {
                    const children = data.filter(s => {
                        const pId = s.parentId?.id || s.parentId;
                        return pId === m.id;
                    });
                    if (children.length > 0) {
                        map[m.id] = children.map(c => ({ label: c.name, value: c.id }));
                    }
                });
                
                setSuperSpecialitiesMap(map);
            } catch (error) {
                console.error("Failed to fetch specialities:", error);
            }
        };
        fetchSpecialities();
    }, []);
    const Degree = [
        { label: 'MD', value: 'md' },
        { label: 'MBBS', value: 'mbbs' },
        { label: 'BDS', value: 'bds' },
        { label: 'MDS', value: 'mds' },
        { label: 'PGDHM', value: 'pgdhm' },
        { label: 'Cert Pedodontics', value: 'cert pedodontics' }
    ]
    const handleSubmit = (values) => {
        setDoctorProfileData(prev => ({
            ...prev,
            professionalInfo: {
                ...values,
                degree: Array.isArray(values.degree) ? values.degree.join(',') : values.degree
            }
        }));
    }
    return (
        <>
            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                innerRef={innerRef}

            >
                {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
                    <Form>
                        <AddContainer>
                            <Component4>
                                <Texts>Experience</Texts>
                                < InputTextComponent
                                    name="experience"
                                    placeholder="Enter"
                                    variant="outlined"
                                    value={values.experience}
                                    onChange={handleChange}
                                />

                            </Component4>
                            <Component4>
                                <Texts>Degree</Texts>
                                <InputSelect
                                    name="degree"
                                    multiple={true}
                                    showNone={false}
                                    options={Degree}
                                    value={values.degree}
                                    onChange={(value) => setFieldValue('degree', value)}
                                    variant="outlined"
                                    placeholder="Select"
                                    sx={{
                                        boxShadow: theme.shadows[1],
                                        '& .MuiOutlinedInput-root': {
                                            height: '52px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            borderRadius: '12px',
                                        },
                                    }}
                                />

                            </Component4>
                            
                            <Component4>
                                <Texts>Speciality</Texts>
                                <Autocomplete
                                    options={specialitiesOptions}
                                    getOptionLabel={(option) => option.label || ''}
                                    value={specialitiesOptions.find(opt => opt.value === values.speciality) || null}
                                    onChange={(event, newValue) => {
                                        setFieldValue('speciality', newValue ? newValue.value : '');
                                        setFieldValue('superSpeciality', '');
                                    }}
                                    renderInput={(params) => (
                                        <TextField 
                                            {...params} 
                                            placeholder="Search Speciality" 
                                            variant="outlined" 
                                            sx={{
                                                boxShadow: theme.shadows[1],
                                                '& .MuiOutlinedInput-root': {
                                                    minHeight: '52px',
                                                    borderRadius: '12px',
                                                    backgroundColor: 'white',
                                                    padding: '0px 14px',
                                                },
                                                '& fieldset': { border: 'none' }, // Remove default border if needed to match design
                                            }}
                                        />
                                    )}
                                />
                            </Component4>

                            {superSpecialitiesMap[values.speciality] && (
                                <Component4>
                                    <Texts>Super Speciality</Texts>
                                    <Autocomplete
                                        options={superSpecialitiesMap[values.speciality] || []}
                                        getOptionLabel={(option) => option.label || ''}
                                        value={(superSpecialitiesMap[values.speciality] || []).find(opt => opt.value === values.superSpeciality) || null}
                                        onChange={(event, newValue) => {
                                            setFieldValue('superSpeciality', newValue ? newValue.value : '');
                                        }}
                                        renderInput={(params) => (
                                            <TextField 
                                                {...params} 
                                                placeholder="Search Super Speciality" 
                                                variant="outlined" 
                                                sx={{
                                                    boxShadow: theme.shadows[1],
                                                    '& .MuiOutlinedInput-root': {
                                                        minHeight: '52px',
                                                        borderRadius: '12px',
                                                        backgroundColor: 'white',
                                                        padding: '0px 14px',
                                                    },
                                                    '& fieldset': { border: 'none' },
                                                }}
                                            />
                                        )}
                                    />
                                </Component4>
                            )}

                            <Component4>
                                <Texts>Licence Info</Texts>
                                < InputTextComponent
                                    name="licenceInfo"
                                    placeholder="Enter"
                                    variant="outlined"
                                    value={values.licenceInfo}
                                    onChange={handleChange}
                                />
                            </Component4>
                            <Component4>
                                <Texts>Professional Experience</Texts>
                                < InputTextComponent
                                    name="professionalExperience"
                                    placeholder="Enter"
                                    variant="outlined"
                                    value={values.professionalExperience}
                                    onChange={handleChange}
                                />
                            </Component4>
                            <Component4>
                                <Texts>Challenges</Texts>
                                < InputTextComponent
                                    name="challenges"
                                    placeholder="Enter"
                                    variant="outlined"
                                    value={values.challenges}
                                    onChange={handleChange}
                                />
                            </Component4>
                            <Component4>
                                <Texts>Awards Achievements</Texts>
                                < InputTextComponent
                                    name="awardsAchievements"
                                    placeholder="Enter"
                                    variant="outlined"
                                    value={values.awardsAchievements}
                                    onChange={handleChange}

                                />
                            </Component4>
                            <Component4>
                                <Texts>Special Procedure</Texts>
                                < InputTextComponent
                                    name="specialProcedure"
                                    placeholder="Enter"
                                    variant="outlined"
                                    value={values.specialProcedure}
                                    onChange={handleChange}
                                />
                            </Component4>
                            <Component4>
                                <Texts>Request To Patients</Texts>
                                < InputTextComponent
                                    name="requestToPatients"
                                    placeholder="Enter"
                                    variant="outlined"
                                    value={values.requestToPatients}
                                    onChange={handleChange}
                                />
                            </Component4>
                        </AddContainer>

                    </Form>
                )}
            </Formik>

        </>
    )
}

export default ProfessionalInfoStep;